import { useState } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';

/**
 * Card Component (Preline UI)
 *
 * Basic card container with optional title.
 * Provides consistent styling for card-based layouts.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Card content
 * @param {string} props.title - Optional card title
 * @param {React.ReactNode} props.headerActions - Optional actions/content to display on the right side of header
 * @param {boolean} props.headerGray - Whether to use gray background for header (default: true)
 * @param {boolean} props.collapsible - Whether the card content can be collapsed (default: false)
 * @param {boolean} props.defaultCollapsed - Whether the card starts collapsed (default: false)
 * @param {number} props.itemCount - Number of items to show as badge when collapsed
 * @param {string} props.className - Additional CSS classes
 */
export function Card({ children, title, headerActions, headerGray = true, collapsible = false, defaultCollapsed = false, itemCount, className = '' }) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  const toggleCollapse = () => {
    if (collapsible) {
      setIsCollapsed(!isCollapsed);
    }
  };

  return (
    <div className={`bg-app border border-border-default rounded-xl overflow-hidden ${className}`}>
      {title && (
        <div
          className={`px-6 py-4 border-b border-border-default ${headerGray ? 'bg-brand/3' : ''} ${collapsible ? 'cursor-pointer hover:bg-brand/5 transition-colors' : ''} flex justify-between items-center`}
          onClick={collapsible ? toggleCollapse : undefined}
        >
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-default">{title}</h3>
            {collapsible && itemCount !== undefined && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-brand/10 text-brand">
                {itemCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {headerActions}
            {collapsible && (
              <Icon
                icon={isCollapsed ? 'lucide:chevron-down' : 'lucide:chevron-up'}
                className="size-5 text-subtle transition-transform"
              />
            )}
          </div>
        </div>
      )}
      {!isCollapsed && (
        <div className="p-6">{children}</div>
      )}
    </div>
  );
}

Card.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  headerActions: PropTypes.node,
  headerGray: PropTypes.bool,
  collapsible: PropTypes.bool,
  defaultCollapsed: PropTypes.bool,
  itemCount: PropTypes.number,
  className: PropTypes.string,
};
